import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Crosshair, Buildings, MagnifyingGlass, Phone, Plus,
  Briefcase, ArrowSquareOut, Copy, UserCirclePlus
} from "@phosphor-icons/react";

const SECTOR_COLORS = {
  education_childcare: { bg: "#EFF6FF", accent: "#1E40AF", badge: "bg-blue-100 text-blue-800" },
  healthcare: { bg: "#F0FDF4", accent: "#166534", badge: "bg-green-100 text-green-800" },
  technology: { bg: "#F5F3FF", accent: "#6D28D9", badge: "bg-violet-100 text-violet-800" },
  finance_professional: { bg: "#FFFBEB", accent: "#B45309", badge: "bg-amber-100 text-amber-800" },
  engineering_construction: { bg: "#FFF1F2", accent: "#BE123C", badge: "bg-rose-100 text-rose-800" },
};

export default function SectorHubPage() {
  const { user } = useAuth();
  const [sector, setSector] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allSectors, setAllSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [showCallScript, setShowCallScript] = useState(false);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: "", website: "", phone: "", email: "", address: "" });
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        if (user?.role === "admin" || user?.role === "team_leader") {
          const { data } = await api.get("/sectors/all");
          setAllSectors(data);
          if (data.length > 0) {
            setSelectedSector(data[0]);
            setSector(data[0]);
          }
        }
        const { data } = await api.get("/sectors/my");
        if (data.sector) {
          setSector(data.sector);
          setTeam(data.team);
          setSelectedSector(data.sector);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [user]);

  const handleGoogleSearch = (query) => {
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, "_blank");
  };

  const handleCreateCompany = async () => {
    if (!companyForm.name) { toast.error("Company name is required"); return; }
    try {
      await api.post("/companies", companyForm);
      toast.success(`${companyForm.name} added to CRM`);
      setCompanyForm({ name: "", website: "", phone: "", email: "", address: "" });
      setShowCompanyForm(false);
    } catch (err) {
      if (err.response?.status === 403) toast.error("You don't have permission to add companies");
      else toast.error("Failed to create company");
    }
  };

  if (loading) return (
    <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 skeleton-shimmer rounded-lg" />)}</div>
  );

  const activeSector = selectedSector || sector;
  const colors = SECTOR_COLORS[activeSector?.key] || SECTOR_COLORS.technology;

  if (!activeSector) return (
    <div data-testid="sector-hub-page" className="flex flex-col items-center justify-center py-20">
      <Crosshair size={48} className="text-[#D1D5DB] mb-3" />
      <h2 className="text-lg font-semibold text-[#0F172A]">No Sector Assigned</h2>
      <p className="text-sm text-[#6B7280] mt-1">Ask your administrator to assign you to a sector team.</p>
    </div>
  );

  return (
    <div data-testid="sector-hub-page" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Sector Intelligence Hub</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {team ? `${team.name} Team` : "Guided search tools and target intelligence"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCallScript(!showCallScript)} variant="outline" className="gap-2 text-sm" data-testid="call-script-btn">
            <Phone size={16} /> Call Script
          </Button>
          <Button onClick={() => setShowCompanyForm(true)} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2 text-sm" data-testid="add-company-btn">
            <Plus size={16} /> Add Company
          </Button>
        </div>
      </div>

      {/* Sector Tabs (Admin/TL only) */}
      {allSectors.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="sector-tabs">
          {allSectors.map(s => (
            <button key={s.key} onClick={() => setSelectedSector(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                selectedSector?.key === s.key
                  ? "bg-[#0F172A] text-white"
                  : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]"
              }`}
              data-testid={`sector-tab-${s.key}`}
            >{s.name}</button>
          ))}
        </div>
      )}

      {/* Call Script Popup */}
      {showCallScript && (
        <div className="bg-[#FFFBEB] border border-[#FCD34D] rounded-lg p-4" data-testid="call-script-panel">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[#92400E]">Call Script — {activeSector.name}</h3>
            <button onClick={() => {navigator.clipboard.writeText(activeSector.call_script); toast.success("Copied!");}}>
              <Copy size={16} className="text-[#92400E]" />
            </button>
          </div>
          <p className="text-sm text-[#78350F] leading-relaxed italic">&ldquo;{activeSector.call_script}&rdquo;</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Target Roles */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="target-roles-panel">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase size={18} style={{ color: colors.accent }} />
            <h2 className="text-sm font-semibold text-[#0F172A]">Target Roles</h2>
            <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}>
              {activeSector.target_roles.length}
            </span>
          </div>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {activeSector.target_roles.map((role, i) => (
              <div key={i} className="flex items-center justify-between group py-1.5 px-2 rounded hover:bg-[#F9FAFB]">
                <span className="text-sm text-[#374151]">{role}</span>
                <button onClick={() => handleGoogleSearch(`${role} jobs`)} className="opacity-0 group-hover:opacity-100 transition-opacity" title="Search Google">
                  <ArrowSquareOut size={14} className="text-[#6B7280]" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Target Employers */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="target-employers-panel">
          <div className="flex items-center gap-2 mb-3">
            <Buildings size={18} style={{ color: colors.accent }} />
            <h2 className="text-sm font-semibold text-[#0F172A]">Target Employers</h2>
            <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colors.accent}15`, color: colors.accent }}>
              {activeSector.target_employers.length}
            </span>
          </div>
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {activeSector.target_employers.map((emp, i) => (
              <div key={i} className="flex items-center justify-between group py-1.5 px-2 rounded hover:bg-[#F9FAFB]">
                <span className="text-sm text-[#374151]">{emp}</span>
                <button onClick={() => handleGoogleSearch(emp)} className="opacity-0 group-hover:opacity-100 transition-opacity" title="Search Google">
                  <ArrowSquareOut size={14} className="text-[#6B7280]" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* One-Click Search */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="search-buttons-panel">
          <div className="flex items-center gap-2 mb-3">
            <MagnifyingGlass size={18} style={{ color: colors.accent }} />
            <h2 className="text-sm font-semibold text-[#0F172A]">One-Click Searches</h2>
          </div>
          <div className="flex flex-col gap-2">
            {activeSector.search_buttons.map((btn, i) => (
              <button key={i} onClick={() => handleGoogleSearch(btn)}
                className="w-full text-left px-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm font-medium text-[#374151] hover:border-[#C5A059] hover:bg-[#FFFBEB] transition-colors flex items-center justify-between group"
                data-testid={`search-btn-${i}`}
              >
                <span>{btn}</span>
                <MagnifyingGlass size={14} className="text-[#D1D5DB] group-hover:text-[#C5A059]" />
              </button>
            ))}
          </div>
          {/* Custom Search */}
          <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
            <div className="flex gap-2">
              <Input placeholder="Custom search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === "Enter" && searchQuery && handleGoogleSearch(searchQuery)}
                className="text-sm" data-testid="custom-search-input" />
              <Button onClick={() => searchQuery && handleGoogleSearch(searchQuery)} size="sm" className="bg-[#C5A059] hover:bg-[#B08A3E]">
                <MagnifyingGlass size={14} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Company Dialog */}
      {showCompanyForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4" data-testid="add-company-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                <UserCirclePlus size={20} className="text-[#C5A059]" /> Add Company to CRM
              </h2>
              <button onClick={() => setShowCompanyForm(false)} className="text-[#6B7280] hover:text-[#374151]">&times;</button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Company Name *" value={companyForm.name} onChange={e => setCompanyForm({...companyForm, name: e.target.value})} data-testid="company-name-input" />
              <Input placeholder="Website" value={companyForm.website} onChange={e => setCompanyForm({...companyForm, website: e.target.value})} data-testid="company-website-input" />
              <Input placeholder="Phone" value={companyForm.phone} onChange={e => setCompanyForm({...companyForm, phone: e.target.value})} data-testid="company-phone-input" />
              <Input placeholder="Email" value={companyForm.email} onChange={e => setCompanyForm({...companyForm, email: e.target.value})} data-testid="company-email-input" />
              <Input placeholder="Address" value={companyForm.address} onChange={e => setCompanyForm({...companyForm, address: e.target.value})} data-testid="company-address-input" />
              <Button onClick={handleCreateCompany} className="w-full bg-[#0F172A] hover:bg-[#1E293B]" data-testid="submit-company-btn">
                Add Company
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
